/**
 * FoodPulse — Food section category → topic-article mapping.
 *
 * The `Food` dropdown in the top nav uses query-string filters
 * (e.g. `/food-pulse/food?category=fish,seafood,shellfish`) rather
 * than path segments. This map lets us:
 *
 *   1. Look up the short intro article to render at the top of
 *      `FoodItemsDirectory` for any category filter (single or
 *      compound).
 *   2. Show sibling category tabs so a reader can jump from
 *      "Fish & Seafood" to "Meat & Poultry" without going back to
 *      the dropdown.
 *
 * The same JSON shape is consumed by the Python seeder at
 * `backend/scripts/seed_food_category_articles.py` — keep the two in
 * sync when you add a new food category.
 */

// Canonical categories — one card per Food-dropdown entry.
// `param` matches what appears in the URL query string exactly.
// `slug`  is the food_articles row we look up for the intro copy.
export const FOOD_CATEGORY_TABS = [
  { slug: "food-fruits",        label: "Fruits",              param: "fruits" },
  { slug: "food-vegetables",    label: "Vegetables",          param: "vegetables,leafy-greens" },
  { slug: "food-meat-poultry",  label: "Meat & Poultry",      param: "meat,poultry" },
  { slug: "food-fish-seafood",  label: "Fish & Seafood",      param: "fish,seafood,shellfish" },
  { slug: "food-dairy-eggs",    label: "Dairy & Eggs",        param: "dairy,eggs" },
  { slug: "food-grains",        label: "Grains & Cereals",    param: "grains" },
  { slug: "food-legumes",       label: "Beans & Lentils",     param: "legumes" },
  { slug: "food-nuts",          label: "Nuts & Seeds",        param: "nuts" },
  { slug: "food-spices-herbs",  label: "Spices & Herbs",      param: "spices,herbs" },
  { slug: "food-fermented",     label: "Fermented Foods",     param: "fermented" },
  { slug: "food-packaged",      label: "Packaged Foods",      param: "packaged" },
  { slug: "food-beverages",     label: "Beverages",           param: "beverages" },
];

// Reverse index — a single-category chip click maps to its parent tab.
// e.g. clicking the "seafood" chip surfaces the "Fish & Seafood" article.
const SINGLE_TO_TAB = {};
FOOD_CATEGORY_TABS.forEach((tab) => {
  tab.param.split(",").forEach((piece) => {
    SINGLE_TO_TAB[piece] = tab;
  });
});

/**
 * Given the raw `category` query-string value, return the tab whose
 * article + sibling nav should be rendered. Returns `null` if the
 * category is empty or unknown.
 *
 * Rules:
 *   1. Exact param match wins (e.g. "fish,seafood,shellfish").
 *   2. Fall back to first single-category match
 *      (e.g. "seafood" → Fish & Seafood).
 */
export function tabForCategoryParam(raw) {
  if (!raw) return null;
  const exact = FOOD_CATEGORY_TABS.find((t) => t.param === raw);
  if (exact) return exact;
  const first = raw.split(",")[0]?.trim().toLowerCase();
  return SINGLE_TO_TAB[first] || null;
}
